import moment from 'moment';
import { Storage } from '@ionic/storage';
import { NetworkProvider } from '../network/network';
import { Observable } from 'rxjs';

/**
 * Service de stockage local CRT
 */
export abstract class LocalStorage {
  /**
   * Nombre minimum de minutes avant de forcer une nouvelle
   * récupération des sites auprès du serveur
   */
  protected serverRequestPeriod: number;
  /**
   * Service Ionic de storage
   */
  protected storage: Storage;
  /**
   * Service Ionic de network
   */
  protected network: NetworkProvider;
  /**
   * Suffix de la clef de stockage pour la date de derniere requete
   * @type {string}
   */
  protected readonly LAST_REQUEST_DATE_SUFFIX = '-lastRequestDate';

  /**
   * Clé dans le local storage pour l'agenda tempo
   */
  protected readonly AGENDA_TEMPO_KEY = 'agenda-tempo';

  /**
   * Clé dans le local storage pour l'agenda OT
   */
  protected readonly AGENDA_OT_KEY = 'agenda-ot';

  /**
   * Clé dans le local storage pour les CRs d'activité interne qui ne sont pas remontés
   */
  protected readonly ACTIVITE_INTERNE_LOCAL_NON_REMONTE_KEY: string = 'activite_interne-non_remonte';

  /**
   * Clé dans le local storage pour les CRs d'activité interne qui ne sont pas remontés
   */
  protected readonly CR_ACTIVITE_INTERNE_LOCAL_NON_REMONTE_KEY: string = 'cr-activite_interne-non_remonte';

  /**
   * Clé dans le local storage pour les CRs d'activité interne
   */
  protected readonly CR_ACTIVITE_INTERNE_LOCAL_KEY: string = 'cr-activite_interne';

  /**
   * Clef de stockage local pour les reglages Donneés
   * @type {string}
   */
  protected readonly ENVOIS_DONNEES_STORAGE_KEY = 'crt-envois-${idSite}-donnees';

  /**
   * Clef de stockage local pour les reglages PH
   * @type {string}
   */
  protected readonly ENVOIS_PH_STORAGE_KEY = 'crt-envois-${idSite}-ph';

  /**
   * Determine si on doit faire un appel au webservice :
   * Si on a une connexion
   * Si la valeur est périmé
   * Si la clé n'existe pas dans le stockage local
   * @param dataStorageKey clef de la valeur stocké
   * @param {boolean} forceServerRequest force l'appel si on a une connexion
   * @param {number} customServerRequestPeriod Remplace la durée de validitée par défaut
   */
  protected shouldUseWebservice(
    dataStorageKey: string,
    forceServerRequest = false,
    customServerRequestPeriod = this.serverRequestPeriod
  ): Observable<boolean> {
    if (isNaN(customServerRequestPeriod)) {
      throw 'Aucune durée de validitée définie';
    }

    return new Observable<boolean>((subscriber) => {
      if (forceServerRequest && this.network.isConnected()) {
        subscriber.next(true);
        subscriber.complete();
      } else {
        this.storage.get(dataStorageKey + this.LAST_REQUEST_DATE_SUFFIX).then((lastRequestDate) => {
          if (!this.network.isConnected() || this.isUpToDate(customServerRequestPeriod, lastRequestDate)) {
            this.storage.get(dataStorageKey).then((e) => {
              subscriber.next(!e);
              subscriber.complete();
            });
          } else {
            subscriber.next(true);
            subscriber.complete();
          }
        });
      }
    });
  }

  protected async invalidateStorageDate(dataStorageKey: string) {
    await this.storage.set(dataStorageKey + this.LAST_REQUEST_DATE_SUFFIX, null);
  }

  /**
   * Met à jour la date de la dernière requete à maintenant
   * @returns {Promise<any>}
   * @param dataStorageKey
   */
  protected async setLastRequestDateToNow(dataStorageKey: string): Promise<any> {
    return this.storage.set(dataStorageKey + this.LAST_REQUEST_DATE_SUFFIX, moment().format());
  }

  /**
   * Controle si la donnée est à jour
   * @param serverRequestPeriod
   * @param lastRequestDate
   * @returns {boolean}
   */
  protected isUpToDate(serverRequestPeriod: number, lastRequestDate: Date) {
    if (!lastRequestDate) {
      return false;
    }
    const elapsedMinutes = moment().diff(lastRequestDate, 'minutes');
    return elapsedMinutes < serverRequestPeriod;
  }

  /**
   * Format une clef de stockage avec les variables
   * @param {string} storageKey
   * @param variables
   * @returns {string}
   */
  protected formatStorageKey(storageKey: string, variables: { [key: string]: string | number }): string {
    Object.keys(variables).forEach((k) => (storageKey = storageKey.replace('${' + k + '}', String(variables[k]))));
    return storageKey;
  }
}
